// Core lib dependencies.
import React from 'react';
import clsx from 'clsx';

import _ from 'lodash';

// Component dependencies.
import SharingInterface from '../../../apps/common/Sharing';
import VotingInterface from '../../../apps/common/Voting';
import { HybridButton } from '../buttons';
import {
  ItemAsset,
  ItemContent,
  ItemCTA,
  ItemDate,
  ItemDetailAsset,
  ItemNetworkIcon,
  ItemProfile,
} from './items/item-components';

// Misc dependencies.
import { capitalize, getLabel } from '../../utils/string';
import * as AssetUtils from '../../utils/asset';
import * as videoPlayers from '../videoPlayers.jsx';

/**
 *  Needed to write a different method for setting classnames for a particular item as I was having issues
 *  with the one above.  Since there are so many UNTESTED files that depend on the method above I'm leaving it
 *  in place.
 *
 **/
function getClassNamesForItem({ item, templateOpts, itemOpts, isOverlay, customVariant, tabIndex }) {
  const xItem = true;
  const xIsFocusable = tabIndex >= 0;
  const displayClass = `xDisplay${capitalize(_.get(item, 'strategy.displayStrategy', 'show'))}`;
  const networkClass = `xNetwork${capitalize(_.get(item, 'meta.network', 'Ugc'))}`;
  let contentClass = `xContent${capitalize(_.get(item, 'meta.contentType', 'Text'))}`;

  // override xContentText class, with original content type class when present
  // note: retweets can't have their own assets
  if (
    !_.isEmpty(item.originalContent) &&
    (_.get(templateOpts, 'itemTemplate') === 'medialayer' || _.get(templateOpts, 'itemTemplate') === 'postcardcarousel')
  ) {
    contentClass = `xContent${capitalize(_.get(item, 'originalContent.meta.contentType', 'Text'))}`;
  }

  const details = isOverlay && _.get(templateOpts, 'details', '').toLowerCase();
  const detailsClass = !!details && `x${capitalize(details)}card`;

  const itemTemplate = _.get(templateOpts, 'itemTemplate', '').toLowerCase();
  const itemTemplateClass = !!itemTemplate && !isOverlay && `x${capitalize(itemTemplate)}`;

  const variant = _.get(templateOpts, 'variant', '');
  const variantClass = !!variant && `xVariant${capitalize(variant)}`;

  const customVariantClass = !!customVariant && `xVariant${capitalize(customVariant)}`;

  const collectionClazz = _.get(templateOpts, 'collectionClass', '');
  const collectionClass = !!collectionClazz && `xClass${capitalize(collectionClazz)}`;

  const itemClazz = _.get(item, 'meta.itemClass', '');
  const itemClass = !!itemClazz && `xClass${capitalize(itemClazz)}`;

  const hasCopy =
    _.has(item, 'content.heading') ||
    _.has(item, 'content.byline') ||
    _.has(item, 'content.content') ||
    _.has(item, 'content.caption');
  const hasMainAsset = !!_.get(item, 'assets.main') || !!_.get(item, 'featureImageAssetRef');

  const hasBGAsset =
    (itemTemplate === 'medialayer' || itemTemplate === 'imagelayer') &&
    (_.get(item, 'assets.main') ||
      _.get(item, 'assets.thumbnail') ||
      _.get(item.originalContent, 'assets.main') ||
      _.get(item.originalContent, 'assets.thumbnail'));

  // Asset status (e.g pending/failed/success for Mux videos).
  let assetStatus;
  if (hasMainAsset) {
    const mainAsset = AssetUtils.assetFromRef(_.get(item, 'assets.main'));

    if (mainAsset.meta && mainAsset.meta.status) {
      assetStatus = 'xStatus' + capitalize(mainAsset.meta.status);
    }
  }

  //  taken from legacy code -> setting 'hasCTA' class if item has content && link && linkTitle
  const hasCTA = _.get(item, 'content') && _.get(item, 'link') && _.get(item, 'content.linkTitle');
  const hasContentCTA = itemHasCTA(item);
  const hasVoting = _.get(itemOpts, 'enableVoting');

  return clsx({
    xItem,
    xIsFocusable,
    [displayClass]:       true,
    [networkClass]:       true,
    [contentClass]:       true,
    hasCopy,
    hasCTA,
    hasMainAsset,
    hasContentCTA,
    hasBGAsset,
    hasVoting,
    [detailsClass]:       !!detailsClass,
    [itemTemplateClass]:  !!itemTemplateClass,
    [variantClass]:       !!variantClass,
    [customVariantClass]: !!customVariantClass,
    [collectionClass]:    !!collectionClass,
    [itemClass]:          !!itemClass,
    [assetStatus]:        !!assetStatus,
  });
}

export { getClassNamesForItem };

/**
 * Returns item content, dependant on availability.
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item: {object} DisplayItem
 * @param type: {string} (main|secondary)
 * @returns {React} ItemContent Elements
 */
function getItemContent({ item, type, heightMap = {}, onExternalLinkClick, truncateHeader }) {
  let _content;

  const { heading, byline, content, caption, description } = _.get(item, 'content', {});
  const network = _.get(item, 'meta.network');
  const tags = _.get(item, 'meta.tags');

  // Main content.
  if (type === 'main') {
    if (heading || byline || content || description) {
      _content = (
        <ItemContent
          type="main"
          heading={heading}
          byline={byline}
          content={content}
          description={description}
          network={network}
          displayedUrls={item.content.displayedUrls}
          onExternalLinkClick={onExternalLinkClick}
          copyBodyHeight={heightMap.body}
          bylineHeight={heightMap.byline}
          hideHeader={heightMap.heading === 0}
          truncateHeader={truncateHeader}
          tags={tags}
        />
      );
    }

    // Secondary content.
  } else if (type === 'secondary') {
    if (caption) {
      _content = <ItemContent type="secondary" caption={caption} />;
    }
  }

  return _content;
}
export { getItemContent };

/**
 * Returns the item's main asset or thumbnail
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item: {object}
 * @returns {React} wrapped image
 */
function getItemAsset({ item, assetUrl, assetUgcUrl, moduleOpts, itemOpts, res }) {
  const displayAssets = AssetUtils.getDisplayAssets(item, assetUrl, assetUgcUrl, res);
  const contentType = _.get(item, 'meta.contentType');
  const thumbnail = AssetUtils.getThumbnail(displayAssets);
  const shouldAutoPlayBool = shouldAutoPlay(_.get(moduleOpts, ['autoPlayConfig']), item);

  if ((item.assets && (item.assets.main || item.assets.thumbnail)) || _.get(item, 'content.featureImageAssetRef')) {
    return (
      <ItemAsset
        id={item.id}
        thumbnail={thumbnail}
        contentType={contentType}
        assetClasses={displayAssets.assetClasses}
        autoPlayConfig={_.get(moduleOpts, 'autoPlayConfig')}
        itemOpts={itemOpts}
        item={item}
        shouldAutoPlay={shouldAutoPlayBool}
      />
    );
  }
  return undefined;
}
export { getItemAsset };

/**
 * Returns the item's detail asset (eg full image or video embed)
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item: {object}
 * @returns {React} wrapped image
 */
function getItemDetailAsset(item, itemOpts, assetUrl, assetUgcUrl) {
  if (_.get(item, 'meta.contentType') !== 'text') {
    return <ItemDetailAsset item={item} itemOpts={itemOpts} assetUrl={assetUrl} assetUgcUrl={assetUgcUrl} />;
  }
  return undefined;
}
export { getItemDetailAsset };

/**
 * Returns item's primary CTA if set
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item: {object} DisplayItem
 * @returns {React} CTA element
 */
function getPrimaryCTA(item) {
  if (item.cta.ctaLink || item.cta.ctaButtonText) {
    return <elements.ItemCTA href={item.cta.ctaLink} target="_blank" linkText={item.cta.ctaButtonText} />;
  }

  if (item.cta.ctaLink || item.cta.ctaButtonText) {
    return <ItemCTA href={item.cta.ctaLink} target="_blank" linkText={item.cta.ctaButtonText} />;
  }

  return undefined;
}
export { getPrimaryCTA };

/**
 * Returns item's timestamp
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item : {object} DisplayItem
 * @returns {React} wrapped date
 */
function getItemTimestamp({ item, onExternalLinkClick }) {
  const timestamp = _.get(item, 'meta.timestamp');
  if (timestamp) {
    const date = new Date(timestamp);
    return <ItemDate date={date} link={_.get(item, 'content.link')} onExternalLinkClick={onExternalLinkClick} />;
  }
}

export { getItemTimestamp };

/**
 * Returns item's network icon
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param network : {string} item's network
 * @returns React Icon element
 */
function getNetworkIcon(network) {
  if (network) {
    return <ItemNetworkIcon network={network} />;
  }
}
export { getNetworkIcon };

/**
 * Returns a profile component
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item : {object} display item
 * @return {React} Wrapped profile component
 */
function getItemProfile({ item, actions, onExternalLinkClick, showOriginalHandle }) {
  const { username, handle, avatar } = _.get(item, 'profile', {});
  if (!!username || !!handle || !!avatar) {
    return (
      <ItemProfile
        username={username}
        handle={handle}
        avatar={avatar}
        network={_.get(item.meta, 'network')}
        actions={actions && actions.profile}
        onExternalLinkClick={onExternalLinkClick}
        originalHandle={showOriginalHandle ? _.get(item, 'originalContent.profile.handle') : null}
      />
    );
  }
}

export { getItemProfile };

/**
 * Returns all the enabled platform actions
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item : {object} DisplayItem
 * @param itemOpts : {object} Item configuration
 * @return {React} Wrapped platform actions components
 */
function getPlatformActions(item, itemOpts) {
  let _actionsContainer;

  // Voting related actions.
  let _voting;
  if (itemOpts.displayVoting) {
    const _showVoteButton = itemOpts.enableVoting,
      _showVoteCount = itemOpts.showVoteCount,
      _voteCount = item.contest.score;

    if (_showVoteButton || _voteCount) {
      _voting = (
        <VotingInterface.Components.Voting
          item={item}
          itemOpts={itemOpts}
          showVoteButton={_showVoteButton}
          showVoteCount={_showVoteCount}
          votes={_voteCount}
        />
      );
    }
  }

  // If any actions, then add container to markup.
  if (_voting) {
    _actionsContainer = <div className="xActionContainer xActionPlatform xFixed"> {_voting} </div>;
  }

  return _actionsContainer;
}
export { getPlatformActions };

/**
 * Returns all the enabled network actions
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item : {object} DisplayItem
 * @param itemOpts : {object} Item configuration
 * @return {React} Wrapped network actions components
 */
function getNetworkActions(item, itemOpts, additionalActions, showLabel, onExternalLinkClick) {
  let _actionsContainer;
  // Loop over actions and generate elements.
  const _networkActions = item.actions.network;

  const _actions = Object.keys(_networkActions).map(function (networkActionKey, index) {
    const _networkAction = _networkActions[networkActionKey];

    const _liClasses = 'xAction xAction' + capitalize(networkActionKey);
    const _iClasses = 'xIcon xIcon' + capitalize(networkActionKey) + capitalize(item.meta.network);
    const _labelKey = ['actions', item.meta.network, networkActionKey].join('.');
    const _ariaLabel = _.get(NGX.lang.actions, [item.meta.network, networkActionKey]);

    let _label;
    if (showLabel) {
      _label = networkActionKey;
      _label = getLabel(_labelKey);
      _label = <span className="xIconLabel"> {_label} </span>;
    }

    // Define click handler and bind to current context.
    const _networkClickHandler = function (networkAction, event) {
      event.preventDefault();
      event.stopPropagation();

      // Get Api service.
      const apiService = NGXD.getServiceContainer().getService('api');
      // Call network API.
      const action = function () {
        apiService.execute.call(this, 'action', networkAction);
        // Integration API
        const params = {
          contentId: item.id,
          url:       item.content.link,
        };
        NGX.Analytics.trigger('action:share:content:' + item.meta.network, params);
        NGX.App.Events.trigger('action:share', item.content.asMutable({ deep: true }));
      }.bind(this);

      if (onExternalLinkClick) {
        onExternalLinkClick(action);
      } else {
        action();
      }
    }.bind(this, _networkAction);

    return (
      <li className={_liClasses} key={index}>
        <HybridButton
          onClick={_networkClickHandler}
          className="xActionHybrid"
          iconClass={_iClasses}
          ariaLabel={_ariaLabel}
        >
          {_label}
        </HybridButton>
      </li>
    );
  });

  // Add any additional actions.
  if (additionalActions) {
    _actions.push(additionalActions);
  }

  // If any actions, then add container to markup.
  if (_actions) {
    _actionsContainer = (
      <div className="xActionContainer xActionNetwork xFixed">
        <ul className="xToolbar xIconToolbar"> {_actions} </ul>{' '}
      </div>
    );
  }

  return _actionsContainer;
}
export { getNetworkActions };

/**
 * Returns all the enabled sharing actions
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item : {object} DisplayItem
 * @param itemOpts : {object} Item configuration
 * @return {React} Wrapped sharing component
 */
function getSharingActions(item, itemOpts, additionalActions, renderMode, externalLinkHandler) {
  let _actionsContainer;

  // Sharing related actions.
  let _sharing;
  if (itemOpts.displaySharing) {
    const sharingConfig = {
      mode:   itemOpts.sharingMode,
      style:  itemOpts.sharingStyle,
      render: renderMode,
    };

    _sharing = (
      <SharingInterface.Components.Sharing
        item={item}
        config={sharingConfig}
        keys={itemOpts.sharingKeys}
        actions={item.actions.sharing}
        additionalActions={additionalActions}
        onExternalLinkClick={externalLinkHandler}
      />
    );
  }

  // Change class name based on overlay mode. This seems to be a hangover from the old code.
  const _classes = 'xActionContainer xFixed ' + (renderMode == 'mode.overlay' ? 'xActionNetwork' : 'xActionSharing');

  // If any actions, then add container to markup.
  if (_sharing) {
    _actionsContainer = <div className={_classes}> {_sharing} </div>;
  }

  return _actionsContainer;
}
export { getSharingActions };

/**
 * Returns any additional actions for an item
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param item : {object} DisplayItem
 * @return {React} Wrapped additional actions components
 */
function getAdditionalActions(item) {
  let _additionalActions;

  // CTA action.
  if (itemHasCTA(item)) {
    _additionalActions = (
      <li key="aa1" className="xAction">
        <i className="xIcon xIconCTA"> </i>{' '}
      </li>
    );
  }

  return _additionalActions;
}
export { getAdditionalActions };

/**
 * Return boolean indicating if item has a cta.
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param  item {object}
 * @returns {boolean}
 */
function itemHasCTA(item) {
  if (
    item.cta &&
    (item.cta.ctaHeadline ||
      item.cta.ctaByline ||
      item.cta.ctaDescription ||
      item.cta.ctaPrice ||
      item.cta.ctaLink ||
      item.cta.ctaButtonText ||
      item.cta.ctaFeatureAssetRef)
  ) {
    return true;
  }

  return false;
}
export { itemHasCTA };

/**
 * Conditionally call external url handler if one is defined.
 *
 * @memberOf Common.Components.DisplayItems.Utils
 *
 * @param  handler {function}
 * @param  url {string}
 * @param  e {object}
 */
function callExternalLinkHandler(handler, url, e) {
  // If there is a valid registered external link handler.
  if (handler) {
    // Call it, passing the url.
    handler(() => window.open(url));

    // Prevent default anchor click behavior from opening new window.
    e.preventDefault();
  }
}
export { callExternalLinkHandler };
function getVideoComponent({ item, itemOpts, shouldAutoPlay, isMuted, styles }) {
  // Get the video type.
  const _videoType = AssetUtils.getAssetVideoType(item);
  const wrapperClasses = clsx({
    xAutoplayEmbed:  shouldAutoPlay,
    xMediaContainer: true,
    xRenderEmbed:    true,
    xAssetVideo:     true,
  });

  if (_videoType) {
    const _videoPlayerClass = videoPlayers[_videoType];
    // Build the video player asset.
    const _videoPlayerElement = (
      <_videoPlayerClass
        item={item}
        itemOpts={itemOpts}
        shouldAutoPlay={shouldAutoPlay}
        isMuted={isMuted}
        styles={styles}
      />
    );
    if (_videoPlayerElement) {
      return <div className={wrapperClasses}>{_videoPlayerElement}</div>;
    }
  }
}

export { getVideoComponent };

function shouldAutoPlay(autoPlayConfig, item = {}) {
  const networksAutoplaying = _.get(autoPlayConfig, 'selectedValues');
  const assetVideoType = AssetUtils.getAssetVideoType(item);

  return !!_.get(autoPlayConfig, 'autoPlayEnabled') && _.includes(networksAutoplaying, assetVideoType);
}

export { shouldAutoPlay };
